import { useState } from "react";
import { createProject } from "../services/projectService";

type LienLimitType = "PER_DRAW" | "AGGREGATED";
type YesNo = "yes" | "no" | "";

const LIEN_TYPE_MAP: Record<LienLimitType, string> = {
  PER_DRAW: "per_draw",
  AGGREGATED: "aggregate",
};

export interface ProjectFormData {
  projName: string;
  projValue: string;
  projDesc: string;
  projAddr: string;
  projCoords: { lat: number; lon: number } | null;
  ownerId?: string;
  ownerPhone: string;
  ownerPrimaryContact: string;
  reqSigner: string;
  reqPhone: string;
  reqEmail: string;
  reqAddr: string;
  gcId?: string;
  gcPhone: string;
  gcPrimaryContact: string;
  architect: string;
  ownerRep: string;
  otherEntities: string;
  lenderBranchId?: string;
  lendingOfficerId?: string;
  ccList: string[];
  preconDate?: string;
  specialReqs: string;
}

export interface ProjectRequirements {
  mtg: YesNo;
  lien: YesNo;
  lienLimit: YesNo;
  lienLimitAmount: string;
  lienLimitType: LienLimitType | "";
  notar: YesNo;
  subInv: YesNo;
  precon: YesNo;
  equity: YesNo;
  equityAmount: string;
  equityDesc: string;
  soft: YesNo;
  changeOrder: YesNo;
  changeOrderThreshold: string;
  retainage: YesNo;
  retainagePercentage: string;
  contingency: YesNo;
  contingencyAmount: string;
  constructionReportAuthoringFeeAmount: string;
  preConstructionReportFeeAmount: string;
}

type CreateProjectResult =
  | { success: true }
  | { success: false; message: string; fields: Record<string, string> | null };

interface ApiError {
  message?: string;
  raw?: {
    fields?: Record<string, string>;
  };
}

const useCreateProject = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCreateProject = async (
    formData: ProjectFormData,
    requirements: ProjectRequirements,
    projectType: string,
  ): Promise<CreateProjectResult> => {
    setLoading(true);
    setError("");

    const payload = {
      type: projectType,
      name: formData.projName.trim(),
      contractValue: {
        currencyCode: "USD",
        amount: parseFloat(formData.projValue) || 0,
      },
      description: formData.projDesc.trim(),
      address: formData.projAddr.trim(),
      ...(formData.projCoords && {
        addressCoordinate: {
          latitude: formData.projCoords.lat,
          longitude: formData.projCoords.lon,
        },
      }),

      stakeholders: {
        ...(formData.ownerId && { ownerId: formData.ownerId }),
        ...(formData.ownerPrimaryContact.trim() && {
          ownerPrimaryContact: formData.ownerPrimaryContact.trim(),
        }),
        ...(formData.reqSigner.trim() && {
          requisitionSignerName: formData.reqSigner.trim(),
        }),
        ...(formData.reqPhone.trim() && {
          requisitionSignerPrimaryPhone: formData.reqPhone.trim(),
          requisitionSignerPhone: formData.reqPhone.trim(),
        }),
        ...(formData.reqEmail.trim() && {
          requisitionSignerEmail: formData.reqEmail.trim(),
        }),
        ...(formData.reqAddr.trim() && {
          requisitionSignerAddress: formData.reqAddr.trim(),
        }),
        ...(formData.gcId && { gcId: formData.gcId }),
        ...(formData.gcPrimaryContact.trim() && {
          gcPrimaryContact: formData.gcPrimaryContact.trim(),
        }),
        ...(formData.architect.trim() && {
          architectId: formData.architect.trim(),
        }),
        ...(formData.ownerRep.trim() && {
          ownerRepresentative: formData.ownerRep.trim(),
        }),
        ...(formData.otherEntities.trim() && {
          otherProjectEntities: { description: formData.otherEntities.trim() },
        }),
        ...(formData.lenderBranchId && { lenderId: formData.lenderBranchId }),
        ...(formData.lendingOfficerId && {
          lendingOfficerId: formData.lendingOfficerId,
        }),
        ...(() => {
          const cc = Array.isArray(formData.ccList)
            ? formData.ccList
            : formData.ccList
              ? [formData.ccList]
              : [];
          return cc.length ? { lenderReportingCc: cc } : {};
        })(),
      },

      settings: {
        inspectionMonthlyMeetings: requirements.mtg === "yes",
        trackLienWaivers: requirements.lien === "yes",
        limitLienWaiver: requirements.lienLimit === "yes",
        ...(requirements.lienLimit === "yes" && {
          lienWaiverLimit: {
            limit: {
              currencyCode: "USD",
              amount: parseFloat(requirements.lienLimitAmount) || 0,
            },
            limitType:
              LIEN_TYPE_MAP[requirements.lienLimitType as LienLimitType] ??
              "aggregate",
          },
        }),
        lienWaiverShouldNotarized: requirements.notar === "yes",
        lienWaiverInvoiceRequired: requirements.subInv === "yes",
        preConstructionReviewRequired: requirements.precon === "yes",
        ...(requirements.precon === "yes" &&
          formData.preconDate && {
            preConstructionReviewDueDate: new Date(
              formData.preconDate,
            ).toISOString(),
          }),
        borrowerEquityRequired: requirements.equity === "yes",
        ...(requirements.equity === "yes" && {
          borrowerEquityDetails: {
            equity: {
              amount: parseFloat(requirements.equityAmount) || 0,
              currencyCode: "USD",
            },
            description: requirements.equityDesc,
          },
        }),
        isSoftCostTracking: requirements.soft === "yes",
        changeOrderApprovalRequired: requirements.changeOrder === "yes",
        ...(requirements.changeOrder === "yes" && {
          changeOrderApprovalThreshold: {
            amount: parseFloat(requirements.changeOrderThreshold) || 0,
            currencyCode: "USD",
          },
        }),
        retainageRequired: requirements.retainage === "yes",
        ...(requirements.retainage === "yes" && {
          retainageRate: parseFloat(requirements.retainagePercentage) || 0,
        }),
        lenderContingencyRequired: requirements.contingency === "yes",
        ...(requirements.contingency === "yes" && {
          lenderContingencyAmount: {
            currencyCode: "USD",
            amount: parseFloat(requirements.contingencyAmount) || 0,
          },
        }),
        constructionReportAuthoringFee: {
          currencyCode: "USD",
          amount: parseFloat(requirements.constructionReportAuthoringFeeAmount) || 0,
        },
        preConstructionReportFee: {
          currencyCode: "USD",
          amount: parseFloat(requirements.preConstructionReportFeeAmount) || 0,
        },
        ...(formData.specialReqs.trim() && {
          otherDetails: formData.specialReqs.trim(),
        }),
      },
    };

    const { error: apiError } = (await createProject(payload)) as {
      error: ApiError | null;
    };

    setLoading(false);

    if (apiError) {
      const message = apiError.message || "Failed to create project.";
      const fields = apiError.raw?.fields ?? null;
      setError(message);
      return { success: false, message, fields };
    }

    return { success: true };
  };

  return { handleCreateProject, loading, error };
};

export default useCreateProject;
